'use client';

import * as React from 'react';
import type * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  type FormProviderProps,
} from 'react-hook-form';

import { cn } from '../lib/cn';
import { Label } from './Label';
import {
  CurrentFormStateContext,
  UnsavedChangesContext,
  useCurrentFormState,
  useUnsavedChangesLabel,
  type TrackedFormState,
} from './unsaved-changes-context';

export function UnsavedChangesProvider({
  children,
  formatDirtyFieldCount,
}: {
  children: React.ReactNode;
  formatDirtyFieldCount?: ((count: number) => React.ReactNode) | undefined;
}) {
  const [forms, setForms] = React.useState<ReadonlyMap<string, TrackedFormState>>(() => new Map());

  const updateForm = React.useCallback((id: string, state: TrackedFormState) => {
    setForms((current) => {
      const previous = current.get(id);
      if (
        previous?.dirtyFieldCount === state.dirtyFieldCount &&
        previous.isDirty === state.isDirty &&
        previous.isSubmitting === state.isSubmitting
      ) {
        return current;
      }
      const next = new Map(current);
      next.set(id, state);
      return next;
    });
  }, []);

  const removeForm = React.useCallback((id: string) => {
    setForms((current) => {
      if (!current.has(id)) return current;
      const next = new Map(current);
      next.delete(id);
      return next;
    });
  }, []);

  const value = React.useMemo(
    () => ({ forms, formatDirtyFieldCount, removeForm, updateForm }),
    [forms, formatDirtyFieldCount, removeForm, updateForm],
  );

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
}

export function FormUnsavedChangesStatus({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  const { dirtyFieldCount } = useCurrentFormState();
  const label = useUnsavedChangesLabel(dirtyFieldCount);
  if (!label) return null;
  return (
    <span className={cn('text-muted-foreground text-xs', className)} {...props}>
      {label}
    </span>
  );
}

function Form<TFieldValues extends FieldValues = FieldValues>({
  children,
  ...props
}: FormProviderProps<TFieldValues>) {
  return (
    <FormProvider {...props}>
      <TrackedFormStateProvider>{children}</TrackedFormStateProvider>
    </FormProvider>
  );
}

function TrackedFormStateProvider({ children }: { children: React.ReactNode }) {
  const formId = React.useId();
  const registry = React.useContext(UnsavedChangesContext);
  const removeForm = registry?.removeForm;
  const updateForm = registry?.updateForm;
  const { dirtyFields, isDirty, isSubmitting } = useFormState();
  const dirtyFieldCount = isDirty ? Math.max(1, countDirtyFields(dirtyFields)) : 0;
  const state = React.useMemo(
    () => ({ dirtyFieldCount, isDirty, isSubmitting }),
    [dirtyFieldCount, isDirty, isSubmitting],
  );

  React.useEffect(() => {
    updateForm?.(formId, state);
  }, [formId, state, updateForm]);

  React.useEffect(
    () => () => {
      removeForm?.(formId);
    },
    [formId, removeForm],
  );

  return (
    <CurrentFormStateContext.Provider value={state}>{children}</CurrentFormStateContext.Provider>
  );
}

function countDirtyFields(value: unknown): number {
  if (value === true) return 1;
  if (Array.isArray(value)) {
    return value.reduce<number>((count, item) => count + countDirtyFields(item), 0);
  }
  if (value && typeof value === 'object') {
    return Object.values(value).reduce<number>((count, item) => count + countDirtyFields(item), 0);
  }
  return 0;
}

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  'use no memo';

  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  if (!itemContext) {
    throw new Error('useFormField should be used within <FormItem>');
  }

  const fieldState = getFieldState(fieldContext.name, formState);

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = React.useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn('space-y-2', className)} {...props} />
      </FormItemContext.Provider>
    );
  },
);
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<
  React.ComponentRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && 'text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<
  React.ComponentRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={!error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-muted-foreground text-[0.8rem]', className)}
      {...props}
    />
  );
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? (error.message ?? '') : children;

  if (!body) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn('text-destructive text-[0.8rem] font-medium', className)}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessage.displayName = 'FormMessage';

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
};
